import React from 'react';
import { Route } from 'react-router-dom';
import { removeRestrictedRoutes } from '@client/helperComponents/restrict/restrictUtils';
import { CAMPAIGN_ROUTE_REDIRECTION_TYPES } from '@client/constants';

export function routeWithSubRoutes(route, index) {
  return (
    <Route
      key={`route-${index}`}
      exact={route.exact || false}
      status={route.status || false}
      path={route.path}
      render={(props) => {
        if (props.staticContext) {
          props.staticContext.status = route.status;
        }
        // remove component property since we dont need it in front-end.
        const { Component, ...routeProperties } = route;

        // Pass the sub-routes down to keep nesting
        return (
          <Component {...props} routes={route.routes} route={routeProperties} />
        );
      }}
    />
  );
}

export function getMainRoutes(userRestrictions) {
  let routes = {
    HOME: {
      TO: '/',
      LABEL: 'Home',
      RESTRICTIONS: {
        SHOULD_BE_LOGGED_IN: false,
      },
    },
    TERMS: {
      TO: '/terms',
      LABEL: 'Terms',
      RESTRICTIONS: {
        SHOULD_BE_LOGGED_IN: false,
      },
    },
    VERIFY_EMAIL: {
      TO: '/verify-email',
      LABEL: 'VERIFY',
      RESTRICTIONS: {
        SHOULD_BE_LOGGED_IN: false,
      },
    },
    SYNC_EMAIL: {
      TO: '/sync-email',
      LABEL: 'SYNC EMAIL',
      RESTRICTIONS: {
        SHOULD_BE_LOGGED_IN: false,
      },
    },
    VERIFY_INVOICE_EMAIL: {
      TO: '/verify-invoice-email',
      LABEL: 'VERIFY INVOICE EMAIL',
      RESTRICTIONS: {
        SHOULD_BE_LOGGED_IN: false,
      },
    },
    FINNISH_TERMS: {
      TO: '/fi/terms',
      LABEL: 'FinnishTerms',
      RESTRICTIONS: {
        SHOULD_BE_LOGGED_IN: false,
      },
    },
    FAQ: {
      TO: '/faq',
      LABEL: 'FAQ',
      RESTRICTIONS: {
        SHOULD_BE_LOGGED_IN: false,
      },
    },
    ADVERTISER: {
      TO: '/advertiser',
      LABEL: 'Advertiser',
      RESTRICTIONS: {
        SHOULD_BE_LOGGED_IN: false,
      },
    },
    FRILANS_FINANS: {
      TO: '/frilansfinans-invoicery',
      LABEL: 'Frilans Finans Invoicery',
      RESTRICTIONS: {
        SHOULD_BE_LOGGED_IN: false,
      },
    },
    ADVERTISER_LOGIN: {
      TO: '/advertiser/login',
      LABEL: 'Advertiser',
      RESTRICTIONS: {
        SHOULD_BE_LOGGED_IN: false,
      },
    },
    LOGIN: {
      TO: '/admin/login',
      LABEL: 'Login',
      RESTRICTIONS: {
        SHOULD_BE_LOGGED_IN: false,
      },
    },
    DASHBOARD: {
      TO: '/admin/dashboard',
      LABEL: 'Dashboard',
      RESTRICTIONS: {
        SHOULD_BE_LOGGED_IN: false,
      },
    },
    REPORT: {
      TO: '/admin/report',
      LABEL: 'report',
      RESTRICTIONS: {
        SHOULD_BE_LOGGED_IN: false,
      },
    },
    ADVERTISER_REPORT: {
      TO: '/advertiser/report',
      LABEL: 'report',
      RESTRICTIONS: {
        SHOULD_BE_LOGGED_IN: false,
      },
    },
    CAMPAIGNS: {
      TO: '/admin/campaigns',
      LABEL: 'Campaigns',
      RESTRICTIONS: {
        SHOULD_BE_LOGGED_IN: false,
      },
      ROUTES: {
        ACTIVE: {
          TO: '/admin/campaigns/active',
          LABEL: 'Active',
          RESTRICTIONS: {
            SHOULD_BE_LOGGED_IN: false,
          },
        },
        DRAFT: {
          TO: '/admin/campaigns/draft',
          LABEL: 'Draft',
          RESTRICTIONS: {
            SHOULD_BE_LOGGED_IN: false,
          },
        },
        COMPLETED: {
          TO: '/admin/campaigns/completed',
          LABEL: 'Completed',
          RESTRICTIONS: {
            SHOULD_BE_LOGGED_IN: false,
          },
        },
        CREATE: {
          TO: '/admin/campaigns/create',
          LABEL: 'Create Campaign',
          RESTRICTIONS: {
            SHOULD_BE_LOGGED_IN: false,
          },
        },
        UPDATE: {
          TO: '/admin/campaigns/update',
          LABEL: 'Update Campaign',
          RESTRICTIONS: {
            SHOULD_BE_LOGGED_IN: false,
          },
        },
      },
    },
    INFLUENCERS: {
      TO: '/admin/influencers',
      LABEL: 'Influencers',
      RESTRICTIONS: {
        SHOULD_BE_LOGGED_IN: false,
      },
    },
    INFLUENCER_DETAILS: {
      TO: '/admin/influencers/:influencerId',
      LABEL: 'Influencer Details',
      RESTRICTIONS: {
        SHOULD_BE_LOGGED_IN: true,
      },
    },
    ADMINS: {
      TO: '/admin/admins',
      LABEL: 'Admins',
      RESTRICTIONS: {
        SHOULD_BE_LOGGED_IN: false,
      },
    },
    NOTIFICATIONS: {
      TO: '/admin/notifications',
      LABEL: 'Notifications',
      RESTRICTIONS: {
        SHOULD_BE_LOGGED_IN: true,
      },
    },
    ADVERTISERS: {
      TO: '/admin/advertisers',
      LABEL: 'Advertisers',
      RESTRICTIONS: {
        SHOULD_BE_LOGGED_IN: false,
      },
      ROUTES: {
        CREATE: {
          TO: '/admin/advertisers/create',
          LABEL: 'CREATE NEW ADVERTISER',
          RESTRICTIONS: {
            shouldBeLoggedIn: true,
          },
        },
        UPDATE: {
          TO: (advertiserId = ':advertiserId') =>
            `/admin/advertisers/${advertiserId}/update`,
          LABEL: 'CREATE NEW ADVERTISER',
          RESTRICTIONS: {
            shouldBeLoggedIn: true,
          },
        },
      },
    },
    INSPIRATION: {
      TO: '/admin/inspiration',
      LABEL: 'Inspiration',
      RESTRICTIONS: {
        SHOULD_BE_LOGGED_IN: false,
      },
      ROUTES: {
        ACTIVE: {
          TO: '/admin/inspiration/active',
          LABEL: 'Active',
          RESTRICTIONS: {
            SHOULD_BE_LOGGED_IN: true,
          },
        },
        DRAFT: {
          TO: '/admin/inspiration/draft',
          LABEL: 'Draft',
          RESTRICTIONS: {
            SHOULD_BE_LOGGED_IN: true,
          },
        },
        INACTIVE: {
          TO: '/admin/inspiration/inactive',
          LABEL: 'Inactive',
          RESTRICTIONS: {
            SHOULD_BE_LOGGED_IN: true,
          },
        },
      },
    },
    ADMIN: {
      TO: '/admin',
      RESTRICTIONS: {
        SHOULD_BE_LOGGED_IN: true,
      },
    },
    SETTINGS: {
      TO: '/admin/settings',
      LABEL: 'Settings',
      RESTRICTIONS: {
        SHOULD_BE_LOGGED_IN: false,
      },
    },
    ADVERTISER_DASHBOARD: {
      TO: '/advertiser/dashboard',
      LABEL: 'Advertiser Dashboard',
      RESTRICTIONS: {
        SHOULD_BE_LOGGED_IN: false,
      },
    },
    CHILD_SAFETY_POLICY: {
      TO: '/child-safety-policy',
      LABEL: 'Child Safety Policy',
      RESTRICTIONS: {
        SHOULD_BE_LOGGED_IN: false,
      },
    },
    PRIVACY_POLICY: {
      TO: '/privacy',
      LABEL: 'Privacy Policy',
      RESTRICTIONS: {
        SHOULD_BE_LOGGED_IN: false,
      },
    },
  };

  if (!userRestrictions) {
    return routes;
  } else {
    return removeRestrictedRoutes(routes, userRestrictions);
  }
}

export function getCampaignRoutes(campaignId, type) {
  switch (type) {
    case CAMPAIGN_ROUTE_REDIRECTION_TYPES.CAMPAIGN_BRIEF_SUBMITTED:
      return `/admin/campaigns/update/${campaignId}`;
    case CAMPAIGN_ROUTE_REDIRECTION_TYPES.CAMPAIGN_SHORTLIST_SUBMITTED:
      return `/admin/campaign/${campaignId}/short-listed`;
    case CAMPAIGN_ROUTE_REDIRECTION_TYPES.CAMPAIGN_PARTICIPANT_DECLINED_INVITATION:
      return `/admin/campaign/${campaignId}/approved`;
    case CAMPAIGN_ROUTE_REDIRECTION_TYPES.CAMPAIGN_ALL_PROFILES_HAVE_SENT_DETAILS:
      return `/admin/campaign/${campaignId}/approved`;
    case CAMPAIGN_ROUTE_REDIRECTION_TYPES.CAMPAIGN_ITEMS_SHIPPED:
      return `/admin/campaign/${campaignId}/approved`;
    case CAMPAIGN_ROUTE_REDIRECTION_TYPES.CAMPAIGN_PARTICIPANT_LATE_TO_SUBMIT_DRAFT:
      return `/admin/campaign/${campaignId}/drafts`;
    case CAMPAIGN_ROUTE_REDIRECTION_TYPES.CAMPAIGN_DRAFT_SUBMITTED:
      return `/admin/campaign/${campaignId}/drafts`;
    case CAMPAIGN_ROUTE_REDIRECTION_TYPES.CAMPAIGN_VERIFY_PUBLISHED_CONTENT:
      return `/admin/campaign/${campaignId}/posts`;
    case CAMPAIGN_ROUTE_REDIRECTION_TYPES.CAMPAIGN_PUBLISHED_CONTENT_HAS_CHANGED:
      return `/admin/campaign/${campaignId}/posts`;
    case CAMPAIGN_ROUTE_REDIRECTION_TYPES.CAMPAIGN_COMPLETED:
      return `/admin/campaign/${campaignId}/posts`;
  }
}
